import type { ExpenseReport } from '@erp/shared';
import { BarChart3, Banknote, Coins, FolderTree, TrendingDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { EmptyState, StatCard } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { getExpenseReport } from '../../lib/expenses.js';
import { formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

/**
 * Standalone expense report (lives under Reports). Posted-only totals, by
 * category, and by month — per currency. Previously a tab inside the Expenses
 * page; moved here so it sits alongside the other financial reports.
 */
export function ExpensesReportPage(): JSX.Element {
  const { t } = useTranslation('expenses');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('report.pageTitle')}</h1>
        <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('report.pageDescription')}</p>
      </header>
      <ExpenseReportView baseCurrency={baseCurrency} />
    </div>
  );
}

/** Analytics: posted-only totals, by category, and by month — per currency. */
function ExpenseReportView({ baseCurrency }: { baseCurrency: string }): JSX.Element {
  const { t, i18n } = useTranslation('expenses');
  const { t: tc } = useTranslation();
  const [report, setReport] = useState<ExpenseReport | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pickedCcy, setPickedCcy] = useState<string | null>(null);
  // Branch lens — server-filtered, so the totals really are that branch's spend.
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouseId, setWarehouseId] = useState('all');

  useEffect(() => {
    void api().get<{ items: Array<{ id: string; name: string }> }>('/warehouses')
      .then((r) => setWarehouses(r.data.items))
      .catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    setReport(null);
    void getExpenseReport({
      from: from || undefined,
      to: to || undefined,
      warehouseId: warehouseId !== 'all' ? warehouseId : undefined,
    }).then(setReport).catch(() => setReport(null));
  }, [from, to, warehouseId]);

  const locale = i18n.language;
  const sortedTotals = useMemo(
    () => (report?.totals ?? []).slice().sort((a, b) => Number(b.total) - Number(a.total)),
    [report],
  );
  const activeCcy = pickedCcy ?? sortedTotals[0]?.currencyCode ?? baseCurrency;
  const total = sortedTotals.find((x) => x.currencyCode === activeCcy);
  const grand = Number(total?.total ?? 0);
  const count = total?.count ?? 0;
  const cats = (report?.byCategory ?? []).filter((c) => c.currencyCode === activeCcy);
  const months = (report?.byMonth ?? []).filter((mo) => mo.currencyCode === activeCcy);
  const maxMonth = Math.max(...months.map((mo) => Number(mo.total)), 1);
  const m = (amt: string | number): string =>
    formatMoney(typeof amt === 'number' ? amt.toFixed(4) : amt, { locale, currency: activeCcy });

  if (!report) return <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-wrap items-center gap-3 p-3">
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.from')}
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.to')}
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" />
        </label>
        {warehouses.length > 1 && (
          <label className="flex items-center gap-1.5 text-xs text-fg-muted">
            {tc('filters.branch', { defaultValue: 'Branch' })}
            <select
              className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="all">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
        )}
        {sortedTotals.length > 1 && (
          <select
            className="ms-auto h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
            value={activeCcy}
            onChange={(e) => setPickedCcy(e.target.value)}
            aria-label={t('form.currency')}
          >
            {sortedTotals.map((x) => <option key={x.currencyCode} value={x.currencyCode}>{x.currencyCode}</option>)}
          </select>
        )}
      </Card>

      {grand === 0 ? (
        <EmptyState icon={<BarChart3 className="h-8 w-8" aria-hidden />} title={t('report.empty')} description={t('report.emptyDescription')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard label={t('report.total')} value={m(grand)} icon={TrendingDown} tone="warning" />
            <StatCard label={t('report.count')} value={formatNumber(count, { locale })} icon={Banknote} />
            <StatCard label={t('report.avg')} value={m(count ? grand / count : 0)} icon={Coins} />
          </div>

          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <FolderTree className="h-4 w-4 text-fg-muted" aria-hidden />{t('report.byCategory')}
            </div>
            <div className="flex flex-col gap-1">
              {cats.map((c) => {
                const pct = grand > 0 ? (Number(c.total) / grand) * 100 : 0;
                return (
                  <div key={c.categoryId ?? 'none'} className="flex items-center gap-3 py-1.5">
                    <span className="w-36 shrink-0 truncate text-sm">{c.categoryName ?? <span className="text-fg-muted">{t('cat.none')}</span>}</span>
                    <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                      <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} aria-hidden />
                    </div>
                    <span className="w-10 shrink-0 text-end text-xs text-fg-muted tabular-nums">{pct.toFixed(0)}%</span>
                    <span className="w-28 shrink-0 text-end font-mono text-sm font-semibold tabular-nums">{m(c.total)}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {months.length > 1 && (
            <Card className="p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <BarChart3 className="h-4 w-4 text-fg-muted" aria-hidden />{t('report.byMonth')}
              </div>
              <div className="flex flex-col gap-1">
                {months.map((mo) => {
                  const pct = (Number(mo.total) / maxMonth) * 100;
                  return (
                    <div key={mo.month} className="flex items-center gap-3 py-1.5">
                      <span className="w-20 shrink-0 font-mono text-xs text-fg-muted tabular-nums">{mo.month}</span>
                      <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                        <div className="h-full rounded-full bg-brand/60" style={{ width: `${pct}%` }} aria-hidden />
                      </div>
                      <span className="w-28 shrink-0 text-end font-mono text-sm font-semibold tabular-nums">{m(mo.total)}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
